import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Paper,
  Divider,
  Alert,
  CircularProgress,
  Avatar,
} from '@mui/material';
import { useAuth } from '../context/AuthContext';
import {
  isValidEmail,
  isValidName,
  isValidPhone,
  isValidPassword,
} from '../utils/validators';

const MAX_AVATAR_SIZE = 5 * 1024 * 1024; // 5 MB
const ALLOWED_AVATAR_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

const Profile = () => {
  const { user, updateProfile, uploadAvatar, deleteAvatar } = useAuth();

  const [formData, setFormData] = useState({
    name: user?.name || '',
    first_name: user?.first_name || '',
    last_name: user?.last_name || '',
    phone: user?.phone || '',
    email: user?.email || '',
  });

  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  const [profileErrors, setProfileErrors] = useState({});
  const [passwordErrors, setPasswordErrors] = useState({});
  const [profileSuccess, setProfileSuccess] = useState(false);
  const [passwordSuccess, setPasswordSuccess] = useState(false);
  const [profileError, setProfileError] = useState('');
  const [passwordError, setPasswordError] = useState('');

  const [loading, setLoading] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);

  // Avatar-specific state
  const [avatarLoading, setAvatarLoading] = useState(false);
  const [avatarError, setAvatarError] = useState('');
  const [avatarSuccess, setAvatarSuccess] = useState(false);

  const handleProfileChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });

    // Clear field error when user types
    if (profileErrors[name]) {
      setProfileErrors({
        ...profileErrors,
        [name]: '',
      });
    }

    // Clear success message
    if (profileSuccess) {
      setProfileSuccess(false);
    }
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData({
      ...passwordData,
      [name]: value,
    });

    // Clear field error when user types
    if (passwordErrors[name]) {
      setPasswordErrors({
        ...passwordErrors,
        [name]: '',
      });
    }

    // Clear success message
    if (passwordSuccess) {
      setPasswordSuccess(false);
    }
  };

  const validateProfileForm = () => {
    const newErrors = {};

    if (!formData.name) {
      newErrors.name = 'Name is required';
    } else if (!isValidName(formData.name)) {
      newErrors.name = 'Name must be at least 2 characters';
    }

    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!isValidEmail(formData.email)) {
      newErrors.email = 'Invalid email format';
    }

    if (formData.phone && !isValidPhone(formData.phone)) {
      newErrors.phone = 'Invalid phone format';
    }

    setProfileErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validatePasswordForm = () => {
    const newErrors = {};

    if (!passwordData.currentPassword) {
      newErrors.currentPassword = 'Current password is required';
    }

    if (!passwordData.newPassword) {
      newErrors.newPassword = 'New password is required';
    } else if (!isValidPassword(passwordData.newPassword)) {
      newErrors.newPassword =
        'Password must be at least 8 characters with at least one letter and one number';
    }

    if (!passwordData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your new password';
    } else if (passwordData.newPassword !== passwordData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setPasswordErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();

    if (!validateProfileForm()) {
      return;
    }

    setLoading(true);
    setProfileError('');
    setProfileSuccess(false);

    try {
      const result = await updateProfile(user.id, {
        name: formData.name,
        first_name: formData.first_name,
        last_name: formData.last_name,
        phone: formData.phone,
      });

      if (result.success) {
        setProfileSuccess(true);
      } else {
        setProfileError(result.error || 'Failed to update profile');
      }
    } catch (error) {
      setProfileError(error.message || 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();

    if (!validatePasswordForm()) {
      return;
    }

    setPasswordLoading(true);
    setPasswordError('');
    setPasswordSuccess(false);

    try {
      const result = await updateProfile(user.id, {
        password: passwordData.newPassword,
        current_password: passwordData.currentPassword,
      });

      if (result.success) {
        setPasswordSuccess(true);
        setPasswordData({
          currentPassword: '',
          newPassword: '',
          confirmPassword: '',
        });
      } else {
        setPasswordError(result.error || 'Failed to update password');
      }
    } catch (error) {
      setPasswordError(error.message || 'An error occurred');
    } finally {
      setPasswordLoading(false);
    }
  };

  // === Avatar handlers ===

  const handleAvatarFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setAvatarError('');
    setAvatarSuccess(false);

    // Client-side validation for type
    if (!ALLOWED_AVATAR_TYPES.includes(file.type)) {
      setAvatarError('Only JPG, PNG, and WebP images are allowed');
      e.target.value = '';
      return;
    }

    // Client-side validation for size (5 MB)
    if (file.size > MAX_AVATAR_SIZE) {
      setAvatarError('File is too large. Maximum size is 5 MB.');
      e.target.value = '';
      return;
    }

    setAvatarLoading(true);
    try {
      const result = await uploadAvatar(user.id, file);

      if (result.success) {
        setAvatarSuccess(true);
      } else {
        setAvatarError(result.error || 'Failed to upload profile image');
      }
    } catch (err) {
      setAvatarError(err.message || 'Failed to upload profile image');
    } finally {
      setAvatarLoading(false);
      // allow selecting the same file again later
      e.target.value = '';
    }
  };

  const handleAvatarDelete = async () => {
    if (!user?.profile_image_url) return;

    setAvatarError('');
    setAvatarSuccess(false);
    setAvatarLoading(true);

    try {
      const result = await deleteAvatar(user.id);

      if (result.success) {
        setAvatarSuccess(true);
      } else {
        setAvatarError(result.error || 'Failed to delete profile image');
      }
    } catch (err) {
      setAvatarError(err.message || 'Failed to delete profile image');
    } finally {
      setAvatarLoading(false);
    }
  };

  const avatarInitial =
    user?.name?.[0]?.toUpperCase() ||
    user?.email?.[0]?.toUpperCase() ||
    '?';

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom>
        My Profile
      </Typography>

      <Grid container spacing={4}>
        {/* Profile Information */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Profile Information
            </Typography>
            <Divider sx={{ mb: 3 }} />

            {/* Avatar section */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                mb: 3,
                gap: 2,
              }}
            >
              <Avatar
                src={user?.profile_image_url || ''}
                alt={user?.name || user?.email || 'User avatar'}
                sx={{ width: 80, height: 80, fontSize: 32 }}
              >
                {avatarInitial}
              </Avatar>

              <Box>
                <Button
                  variant="outlined"
                  component="label"
                  disabled={avatarLoading}
                >
                  {avatarLoading ? (
                    <CircularProgress size={20} />
                  ) : (
                    'Upload Photo'
                  )}
                  <input
                    hidden
                    type="file"
                    accept="image/png, image/jpeg, image/webp"
                    onChange={handleAvatarFileChange}
                  />
                </Button>

                {user?.profile_image_url && (
                  <Button
                    color="error"
                    sx={{ ml: 1 }}
                    onClick={handleAvatarDelete}
                    disabled={avatarLoading}
                  >
                    Remove
                  </Button>
                )}

                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 1 }}
                >
                  Max size 5 MB. JPG, PNG, or WebP.
                </Typography>
              </Box>
            </Box>

            {/* Avatar alerts */}
            {avatarError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {avatarError}
              </Alert>
            )}

            {avatarSuccess && (
              <Alert severity="success" sx={{ mb: 2 }}>
                Profile photo updated successfully!
              </Alert>
            )}

            {/* Profile alerts */}
            {profileError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {profileError}
              </Alert>
            )}

            {profileSuccess && (
              <Alert severity="success" sx={{ mb: 2 }}>
                Profile updated successfully!
              </Alert>
            )}

            <Box component="form" onSubmit={handleProfileSubmit} noValidate>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    required
                    fullWidth
                    id="name"
                    label="Full Name"
                    name="name"
                    value={formData.name}
                    onChange={handleProfileChange}
                    error={!!profileErrors.name}
                    helperText={profileErrors.name}
                    disabled={loading}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    id="first_name"
                    label="First Name"
                    name="first_name"
                    value={formData.first_name}
                    onChange={handleProfileChange}
                    disabled={loading}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    id="last_name"
                    label="Last Name"
                    name="last_name"
                    value={formData.last_name}
                    onChange={handleProfileChange}
                    disabled={loading}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    id="phone"
                    label="Phone Number"
                    name="phone"
                    value={formData.phone}
                    onChange={handleProfileChange}
                    error={!!profileErrors.phone}
                    helperText={profileErrors.phone}
                    disabled={loading}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    required
                    fullWidth
                    id="email"
                    label="Email Address"
                    name="email"
                    value={formData.email}
                    onChange={handleProfileChange}
                    error={!!profileErrors.email}
                    helperText={profileErrors.email}
                    disabled={true} // Email cannot be changed
                  />
                </Grid>
              </Grid>
              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mt: 3 }}
                disabled={loading}
              >
                {loading ? (
                  <CircularProgress size={24} />
                ) : (
                  'Update Profile'
                )}
              </Button>
            </Box>
          </Paper>
        </Grid>

        {/* Change Password */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Change Password
            </Typography>
            <Divider sx={{ mb: 3 }} />

            {passwordError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {passwordError}
              </Alert>
            )}

            {passwordSuccess && (
              <Alert severity="success" sx={{ mb: 2 }}>
                Password updated successfully!
              </Alert>
            )}

            <Box component="form" onSubmit={handlePasswordSubmit} noValidate>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    required
                    fullWidth
                    name="currentPassword"
                    label="Current Password"
                    type="password"
                    id="currentPassword"
                    value={passwordData.currentPassword}
                    onChange={handlePasswordChange}
                    error={!!passwordErrors.currentPassword}
                    helperText={passwordErrors.currentPassword}
                    disabled={passwordLoading}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    required
                    fullWidth
                    name="newPassword"
                    label="New Password"
                    type="password"
                    id="newPassword"
                    value={passwordData.newPassword}
                    onChange={handlePasswordChange}
                    error={!!passwordErrors.newPassword}
                    helperText={passwordErrors.newPassword}
                    disabled={passwordLoading}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    required
                    fullWidth
                    name="confirmPassword"
                    label="Confirm New Password"
                    type="password"
                    id="confirmPassword"
                    value={passwordData.confirmPassword}
                    onChange={handlePasswordChange}
                    error={!!passwordErrors.confirmPassword}
                    helperText={passwordErrors.confirmPassword}
                    disabled={passwordLoading}
                  />
                </Grid>
              </Grid>
              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mt: 3 }}
                disabled={passwordLoading}
              >
                {passwordLoading ? (
                  <CircularProgress size={24} />
                ) : (
                  'Change Password'
                )}
              </Button>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Profile;
